import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Building2, MapPin, FileText } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import DashboardLayout from '../components/DashboardLayout';

export default function RasAlKhaimahSetup() {
  const navigate = useNavigate();
  const { language } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const authorities = [
    {
      id: 'rakez',
      name_ar: 'راكز رأس الخيمة',
      name_en: 'RAKEZ Ras Al Khaimah',
      description_ar: 'المنطقة الاقتصادية رأس الخيمة',
      description_en: 'Ras Al Khaimah Economic Zone',
      color: '#3B82F6',
      icon: Building2,
      route: '/control/company-setup/ras-al-khaimah/rakez'
    },
    {
      id: 'ded-rak',
      name_ar: 'دائرة التنمية الاقتصادية رأس الخيمة',
      name_en: 'DED Ras Al Khaimah',
      description_ar: 'دائرة التنمية الاقتصادية',
      description_en: 'Department of Economic Development',
      color: '#10B981',
      icon: FileText,
      route: '/control/company-setup/ras-al-khaimah/ded'
    }
  ];

  return (
    <DashboardLayout
      title="إمارة رأس الخيمة"
      titleEn="Ras Al Khaimah"
      subtitle="اختر جهة الترخيص"
      subtitleEn="Select licensing authority"
      backPath="/control/company-setup"
      breadcrumb={[
        { labelAr: 'CLOUDGM FZ LLC', labelEn: 'CLOUDGM FZ LLC', path: '/dashboard?view=cloudgm' },
        { labelAr: 'منصة العملاء', labelEn: 'Customers Platform', path: '/dashboard?view=cloudgm&platform=customers' },
        { labelAr: 'التحكم والمعلومات (منصة العملاء)', labelEn: 'Control & Info (Customers)', path: '/control/cloudgm' },
        { labelAr: 'تأسيس الشركات', labelEn: 'Company Setup', path: '/control/company-setup' },
        { labelAr: 'إمارة رأس الخيمة', labelEn: 'Ras Al Khaimah' }
      ]}
    >
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Section Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
            <MapPin className="h-6 w-6 text-cyan-500" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">
              {language === 'ar' ? 'جهات الترخيص' : 'Licensing Authorities'}
            </h2>
            <p className="text-sm text-[#71717A]">
              {authorities.length} {language === 'ar' ? 'جهات متاحة' : 'authorities available'}
            </p>
          </div>
        </div>

        {/* Authorities Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {authorities.map((authority, index) => {
            const Icon = authority.icon;
            return (
              <motion.div
                key={authority.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                onClick={() => navigate(authority.route)}
                className="group relative bg-white/[0.02] backdrop-blur-xl rounded-2xl p-8 border border-white/10 hover:border-[#d4af37]/50 transition-all duration-300 cursor-pointer hover:-translate-y-1 hover:shadow-[0_8px_32px_rgba(212,175,55,0.12)] overflow-hidden"
                data-testid={`authority-${authority.id}`}
              >
                {/* Hover Glow */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#d4af37]/0 via-[#d4af37]/0 to-[#d4af37]/0 group-hover:from-[#d4af37]/5 group-hover:via-transparent group-hover:to-[#d4af37]/5 transition-all duration-500" />
                
                {/* Glass Shine */}
                <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                
                <div className="relative flex items-start gap-4">
                  <div 
                    className="w-16 h-16 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 border"
                    style={{ 
                      backgroundColor: `${authority.color}15`,
                      borderColor: `${authority.color}30`
                    }}
                  >
                    <Icon className="h-8 w-8" style={{ color: authority.color }} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-white mb-2 group-hover:text-[#d4af37] transition-colors">
                      {language === 'ar' ? authority.name_ar : authority.name_en}
                    </h3>
                    <p className="text-sm text-[#71717A]">
                      {language === 'ar' ? authority.description_ar : authority.description_en}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>
    </DashboardLayout>
  );
}
